import { useMemo } from "react";
import { useCurrentScale } from "remotion";

const HANDLE_SIZE = 14;
const EDGE_HANDLE_WIDTH = 8;
const EDGE_HANDLE_HEIGHT = 20;

export const RESIZE_HANDLE_ID_PREFIX = "resize-handle-";

export const RESIZE_HANDLE_TYPES = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
  "top",
  "left",
  "right",
  "bottom",
] as const;

export type ResizeHandleType = (typeof RESIZE_HANDLE_TYPES)[number];

export const ResizeHandle: React.FC<{
  type: ResizeHandleType;
  currentResizeHandleType: ResizeHandleType | null;
  isEdgeHandleVisible?: boolean;
  isMoving: boolean;
}> = ({ type, currentResizeHandleType, isEdgeHandleVisible, isMoving }) => {
  const scale = useCurrentScale() || 1;
  const size = Math.round(HANDLE_SIZE / scale);
  const borderRadius = HANDLE_SIZE / scale;
  const borderSize = 1 / scale;

  const edgeWidth = Math.round(EDGE_HANDLE_WIDTH / scale);
  const edgeHeight = Math.round(EDGE_HANDLE_HEIGHT / scale);
  const edgeBorderRadius = Math.max(edgeWidth, edgeHeight) / 2;

  const isEdgeHandle = ["top", "left", "right", "bottom"].includes(type);

  const baseStyle: React.CSSProperties = useMemo(() => {
    if (isEdgeHandle) {
      return {
        position: "absolute",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };
    }

    return {
      position: "absolute",
      height: size,
      width: size,
      border: `${borderSize}px solid #BCBCBC`,
      borderRadius: borderRadius,
    };
  }, [borderRadius, borderSize, size, isEdgeHandle]);

  const offset = size / 2;
  const edgeOffsetWidth = Math.round((EDGE_HANDLE_WIDTH / 2 + 1) / scale);

  const style: React.CSSProperties = useMemo(() => {
    if (type === "top-left") {
      return {
        ...baseStyle,
        left: -offset,
        top: -offset,
        cursor: "nwse-resize",
      };
    }

    if (type === "top-right") {
      return {
        ...baseStyle,
        top: -offset,
        right: -offset,
        cursor: "nesw-resize",
      };
    }

    if (type === "bottom-left") {
      return {
        ...baseStyle,
        left: -offset,
        bottom: -offset,
        cursor: "nesw-resize",
      };
    }

    if (type === "bottom-right") {
      return {
        ...baseStyle,
        right: -offset,
        bottom: -offset,
        cursor: "nwse-resize",
      };
    }

    if (type === "top") {
      return {
        ...baseStyle,
        width: "100%",
        height: edgeWidth,
        top: -edgeOffsetWidth,
        left: 0,
        cursor: "ns-resize",
      };
    }

    if (type === "bottom") {
      return {
        ...baseStyle,
        width: "100%",
        height: edgeWidth,
        bottom: -edgeOffsetWidth,
        left: 0,
        cursor: "ns-resize",
      };
    }

    if (type === "left") {
      return {
        ...baseStyle,
        width: edgeWidth,
        height: "100%",
        left: -edgeOffsetWidth,
        top: 0,
        cursor: "ew-resize",
      };
    }

    if (type === "right") {
      return {
        ...baseStyle,
        width: edgeWidth,
        height: "100%",
        right: -edgeOffsetWidth,
        top: 0,
        cursor: "ew-resize",
      };
    }

    throw new Error("Unknown type: " + JSON.stringify(type));
  }, [baseStyle, type, offset, edgeWidth, edgeOffsetWidth]);

  const visibleHandleStyle: React.CSSProperties = useMemo(() => {
    if (!isEdgeHandle) return {};

    return {
      border: `${borderSize}px solid #BCBCBC`,
      borderRadius: edgeBorderRadius,
      width: type === "left" || type === "right" ? edgeWidth : edgeHeight,
      height: type === "left" || type === "right" ? edgeHeight : edgeWidth,
      transition: "background-color 0.2s ease",
    };
  }, [isEdgeHandle, borderSize, edgeBorderRadius, edgeWidth, edgeHeight, type]);

  if (
    isMoving ||
    (currentResizeHandleType !== null && currentResizeHandleType !== type)
  ) {
    return null;
  }

  const isActive = currentResizeHandleType === type;

  return (
    <div
      id={`${RESIZE_HANDLE_ID_PREFIX}${type}`}
      style={style}
      className={`${isEdgeHandle ? "group" : `${isActive ? "bg-tertiary-400" : "bg-white"} hover:bg-tertiary-400`}`}
    >
      {isEdgeHandle && isEdgeHandleVisible && (
        <div
          className={`${isActive ? "bg-tertiary-400" : "bg-white"} pointer-events-none group-hover:bg-tertiary-400`}
          style={visibleHandleStyle}
        />
      )}
    </div>
  );
};
